import { useState } from "react";
import { CheckCircle2, Download, ExternalLink, Loader2 } from "lucide-react";
import { downloadFileFromUrl } from "../utils/downloadUtil";
import { formatDateTime } from "../utils/dateUtils";

const SUMMARY_MAX_CHARS = 280;

const ResultDisplay = ({ result, compact = false }) => {
  const [isDownloading, setIsDownloading]     = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  if (!result) return null;

  const stats = [
    { label: "Status",      value: result.status || "Success" },
    { label: "Work Items",  value: result.total_work_items ?? 0 },
    { label: "Diffs",       value: result.total_diffs ?? 0 },
    { label: "Generated",   value: formatDateTime(result.generated_at) || "-", small: true },
  ];

  const rawSummary   = result.executive_summary || "";
  const isLong       = rawSummary.length > SUMMARY_MAX_CHARS;
  const displaySummary = isLong && !summaryExpanded
    ? rawSummary.slice(0, SUMMARY_MAX_CHARS).trimEnd() + "…"
    : rawSummary || "No executive summary available.";

  const handleDownloadClick = async (e) => {
    e.preventDefault();
    if (!result.published_url) return;
    setIsDownloading(true);
    try {
      await downloadFileFromUrl(result.published_url);
    } catch {
      window.open(result.published_url, "_blank");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`w-full! bg-white! border border-slate-100! space-y-5! text-left! ${compact ? "mt-0! p-0! rounded-2xl! shadow-none!" : "mt-12! p-8! rounded-4xl! shadow-2xl!"}`}>
      <div className="flex items-center gap-4 justify-start!">
        <div className="w-14 h-14 bg-green-50! rounded-2xl flex items-center justify-center">
          <CheckCircle2 className="text-green-500!" size={32} />
        </div>
        <div className="text-left!">
          <h3 className="text-2xl font-bold text-slate-800 m-0! text-left!">
            Generation Successful
          </h3>
          <p className="text-sm text-slate-400 m-0! mt-1! text-left!">
            Published and metadata is ready.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-5 border-y border-slate-100!">
        {stats.map((item, i) => (
          <div key={i} className="flex flex-col items-start!">
            <span className="text-[11px] uppercase font-black text-slate-400 mb-2! text-left!">
              {item.label}
            </span>
            <span className={`font-bold text-slate-700! ${item.small ? "text-[11px]" : "text-lg"} text-left!`}>
              {item.value}
            </span>
          </div>
        ))}
      </div>

      <div className="bg-slate-50! p-5 rounded-3xl border border-slate-100! text-left!">
        <span className="text-[11px] uppercase font-black text-slate-400 block mb-4! text-left!">
          Executive Summary
        </span>
        <p className="text-base text-slate-600! leading-relaxed font-medium m-0! italic text-left!">
          {displaySummary}
        </p>
        {isLong && (
          <button
            type="button"
            onClick={() => setSummaryExpanded((v) => !v)}
            className="mt-3! text-[13px] font-semibold text-blue-600 hover:text-blue-800 border-0 bg-transparent cursor-pointer p-0!"
          >
            {summaryExpanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-4 w-full!">
        <a
          href={result.published_url || "#"}
          target="_blank"
          rel="noreferrer"
          className="h-11.5! px-8! flex items-center justify-center gap-2 bg-white! text-slate-700! border! border-slate-200! font-semibold rounded-xl! hover:bg-slate-50! transition-all text-sm no-underline! shadow-sm cursor-pointer!"
        >
          <ExternalLink size={20} /> View Document
        </a>
        <button
          onClick={handleDownloadClick}
          disabled={isDownloading || !result.published_url}
          className="h-11.5! px-8! rounded-xl! bg-[#1d4ed8]! text-white! font-semibold text-sm flex items-center gap-2 hover:bg-[#1e40af]! transition-all active:scale-[0.98] disabled:opacity-50 border-0! cursor-pointer shadow-sm"
        >
          {isDownloading ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
          {isDownloading ? "Downloading..." : "Download File"}
        </button>
      </div>
    </div>
  );
};

export default ResultDisplay;
